/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { Button, Sidebar, Table, Spacer, theme } from "@nordcloud/gnui";
import { DropdownWithFetch } from "~/components/DropdownWithFetch/DropdownWithFetch";
import { useShowError } from "~/components/Error/useShowError";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { ToolUnassign } from "~/components/Tooling/ToolUnassign";
import { EnvironmentRouteParams, Tool } from "~/models";
import { ROUTES } from "~/routing";
import { useAxios, generateActionSuccessText, displayYesOrNo } from "~/tools";

type ManageServiceToolsProps = {
  serviceId: string;
  attachedTools: Array<Tool>;
  refetch: () => void;
  setIsPopover: React.Dispatch<React.SetStateAction<boolean>>;
};

const IconWrap = styled.div`
  transform: translateY(0.3rem);
`;
function ManageServiceTools({
  serviceId,
  attachedTools,
  refetch,
  setIsPopover,
}: ManageServiceToolsProps) {
  const state = useParams<EnvironmentRouteParams>();
  const { environmentId } = state;
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [toolId, setToolId] = React.useState("");
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async () => {
    const organizeValues = {
      toolId,
    };
    try {
      await Axios().post(
        `/environments/${environmentId}/services/${serviceId}/tools`,
        organizeValues
      );
      showSuccess(generateActionSuccessText(`Tool`)()("assigned")());
      refetch();
    } catch (e) {
      showError(e.response);
    } finally {
      setToolId("");
      setIsSidebarOpen(false);
      setIsPopover(false);
    }
  };

  const deleteCallback = () => {
    refetch();
    setIsSidebarOpen(false);
    setIsPopover(false);
  };
  const { handleSubmit } = useForm();
  const toolIds = attachedTools.map((item: Tool) => item.id);

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        size="md"
        icon="settings"
        severity="low"
        css={{ backgroundColor: theme.color.background.ui01 }}
      >
        Manage Service Tools
      </Button>
      <Sidebar
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        title="Manage Service Tools"
      >
        {attachedTools?.length !== 0 && (
          <>
            <Table small hoverline>
              <Table.thead>
                <Table.tr>
                  <Table.th css={{ width: "100%" }}>Tool Name</Table.th>
                  <Table.th css={{ textAlign: "center" }}>Internal</Table.th>
                  <Table.th css={{ textAlign: "center" }}>Actions</Table.th>
                </Table.tr>
              </Table.thead>
              <Table.tbody>
                {attachedTools?.map((tool: Tool) => (
                  <Table.tr key={tool.id}>
                    <Table.td css={{ width: "100%" }}>
                      <Link to={`${ROUTES.tool.index}/${tool.id}`}>
                        {tool.name}
                      </Link>
                    </Table.td>
                    <Table.td css={{ textAlign: "center" }}>
                      <IconWrap>{displayYesOrNo(tool.internal)}</IconWrap>
                    </Table.td>
                    <Table.td css={{ textAlign: "center" }}>
                      <ToolUnassign
                        serviceId={serviceId}
                        deleteCallback={deleteCallback}
                        tool={tool}
                      />
                    </Table.td>
                  </Table.tr>
                ))}
              </Table.tbody>
            </Table>
            <Spacer height="1rem" />
          </>
        )}
        <DropdownWithFetch
          name="Tools"
          endpoint="tools"
          setId={setToolId}
          dataToFilter={toolIds}
        />
        <Spacer height="1rem" />
        <FlexContainer>
          <Button
            disabled={toolId.length === 0}
            onClick={handleSubmit(onSubmit)}
          >
            Assign
          </Button>
          <Button
            severity="low"
            css={{ marginLeft: "auto" }}
            onClick={() => setIsSidebarOpen(false)}
          >
            Cancel
          </Button>
        </FlexContainer>
      </Sidebar>
    </>
  );
}

export { ManageServiceTools };
